<!-- ProductDetail.vue -->
<template>
  <div v-if="!productDetails" class="pd-loading">
    <h1 v-if="loadingH1" class="pd-loading-title">{{ loadingH1 }}</h1>
    <span class="pd-spinner"></span>
    <p>載入中…</p>
  </div>
  <div class="product-detail-page" v-else>
    <nav class="breadcrumb">
      <!-- <router-link to="/">首頁</router-link> &gt; -->
      <router-link to="/products">所有產品</router-link> &gt;
      <span>{{ productDetails.title }}</span>
    </nav>
    <!-- 左右兩側排版：圖片/影片 + 主要資訊 -->
    <div class="detail-top-section">
      <!-- 左側媒體區 -->
      <div class="detail-media">
        <div class="media-viewer">
          <!-- 主視區 -->
          <div class="main-display">
            <!-- 左箭頭 -->
            <button v-if="hasPrev" class="arrow arrow-left" @click="prevMedia">
              ‹
            </button>

            <template v-if="currentMedia.media_type === 'facebook'">
              <!-- FB Reels or Watch -->
              <div class="video-container">
                <div class="fb-video" :key="`fb-${currentIndex}-${Date.now()}`" :data-href="currentMedia.media_url"
                  data-width="500" data-show-text="false"></div>
              </div>
            </template>

            <!-- 如果 currentMedia 是影片 -->
            <template v-else-if="currentMedia.media_type === 'youtube'">
              <!-- YouTube -->
              <iframe v-if="currentMedia.youtube_id" class="responsive-iframe"
                :src="`https://www.youtube.com/embed/${currentMedia.youtube_id}`" frameborder="0"
                allowfullscreen></iframe>
              <!-- mp4 -->
              <video v-else controls class="responsive-video" :src="currentMedia.media_url"></video>
            </template>

            <!-- 如果 currentMedia 是圖片 + 放大鏡 -->
            <template v-else-if="currentMedia.media_type === 'image'">
              <div class="magnifier-container" @mousemove="moveLens" @mouseleave="hideLens" ref="magnifierWrapper">
                <img class="main-image" :src="currentMedia.media_url" ref="mainImgRef"
                  :alt="`${$COMPANY_NAME} (${$COMPANY_NAMEn}) ${productDetails.title}`" />
                <!-- 放大鏡鏡頭 (Lens) -->
                <div class="magnifier-lens" v-if="lensVisible" :style="lensStyle"></div>

                <!-- 放大後顯示區 -->
                <div class="magnifier-result" v-if="lensVisible" :style="resultStyle"></div>
              </div>
            </template>

            <!-- 沒有媒體 -->
            <div v-else class="no-media">
              找不到媒體
            </div>
            <!-- 右箭頭 -->
            <button v-if="hasNext" class="arrow arrow-right" @click="nextMedia">
              ›
            </button>
          </div>

          <!-- 縮略圖列表 -->
          <div class="thumbnail-list">
            <div class="thumbnail" v-for="(media, i) in productDetails.pMedias" :key="i" @click="currentIndex = i">
              <!-- 如果是影片 (YouTube) -->
              <img v-if="media.media_type === 'youtube' && media.youtube_id"
                :src="getYoutubeThumbnail(media.youtube_id)"
                :alt="`${productDetails.title} 產品影片`" />
              <!-- 若是 facebook => 看你有無縮略圖，或直接用 Facebook Graph ? 這要另行處理 -->
              <img v-else-if="media.media_type === 'facebook'" src="/img/fb-placeholder.jpg"
                :alt="`${$COMPANY_NAME} (${$COMPANY_NAMEn}) ${productDetails.title}`" />
              <!-- 若是 mp4 或圖片，就直接用 media_url 當縮圖 -->
              <img v-else :src="media.media_url"
                :alt="`${$COMPANY_NAME} (${$COMPANY_NAMEn}) ${productDetails.title}`" />
            </div>
          </div>
        </div>
      </div>

      <!-- 右側商品資訊 -->
      <div class="detail-info">
        <!-- 標題 -->
        <h1>{{ productDetails.title }}</h1>

        <!-- 運送 / 付款方式 -->
        <div class="shipping-info">
          <h4>運送 / 付款方式</h4>
          <ul>
            <li v-for="method in shippingMethods" :key="method.id">
              <img v-if="method.icon_url" :src="method.icon_url" :alt="method.name" />
              {{ method.name }}，運費 NT$ {{ method.shipping_cost }}，滿 NT$ {{ method.free_shipping_threshold }} 免運
            </li>
          </ul>
        </div>

        <!-- 價格顯示 -->
        <div class="price">
          <!-- (1) 若有選規格 → 顯示該規格原價 / 折扣價 -->
          <template v-if="selectedVariant">
            <del v-if="Number(selectedVariant.original_price) !== Number(selectedVariant.discount_price)">
              NT${{ selectedVariant.original_price }}
            </del>
            <span class="discount-price">
              NT${{ selectedVariant.discount_price }}
            </span>
          </template>

          <!-- (2) 無選規格 → 顯示「最低 ~ 最高」 -->
          <template v-else>
            <del v-if="productDetails.price !== productDetails.discount_price">
              NT${{ productDetails.price }} ~ {{ productDetails.highest_price }}
            </del>
            <span class="discount-price">
              NT${{ productDetails.discount_price }} ~ {{ productDetails.highest_discount }}
            </span>
          </template>
        </div>

        <!-- 折扣券提示 -->
        <div v-if="bestCoupon" class="pd-coupon-hint">
          🏷️ 使用折扣券最低券後
          <strong>NT${{ couponAfterPrice.toLocaleString() }}</strong>
          <span>（滿 NT${{ bestCoupon.min_amount.toLocaleString() }} 享 {{ Number(bestCoupon.discount_rate) }} 折）</span>
          <span v-if="bestCoupon.days_left != null" class="pd-coupon-expire">⏳ 倒數 {{ bestCoupon.days_left }} 天</span>
        </div>

        <div class="stock-sold-wrapper" v-if="productDetails.sold_quantity > 0">
          <div class="info-box sold-box">
            <i class="fas fa-fire-alt"></i>
            <span class="label">已售出&ensp;</span>
            <span class="number">{{ productDetails.sold_quantity }}</span>
            <span class="label">&ensp;{{ productDetails.unit }}</span>
          </div>
          <!-- <div class="info-box stock-box">
            <i class="fas fa-box-open"></i>
            <span class="label">剩餘庫存&ensp;</span>
            <span class="number">{{productDetails.stock}}</span>
            <span class="label">&ensp;{{ productDetails.unit }}</span>
          </div> -->
        </div>

        <!-- 目前這批的製造日期、有效日期（後台「商品管理」填，換批時更新；沒填就不顯示） -->
        <p class="pd-batch-dates" v-if="productDetails.manufacture_date || productDetails.expiry_date">
          <span v-if="productDetails.manufacture_date">製造日期 {{ productDetails.manufacture_date }}</span>
          <span v-if="productDetails.expiry_date">有效日期 {{ productDetails.expiry_date }}</span>
        </p>

        <!-- 規格選擇區 (若 productDetails.Variants 有多筆) -->
        <div class="variant-choices" v-if="productDetails.Variants && productDetails.Variants.length > 1">
          <p>請選擇規格：</p>
          <div class="variant-item" v-for="(variant, vIdx) in productDetails.Variants" :key="vIdx"
            @click="selectVariant(variant)" :class="{ 'is-active': selectedVariant === variant, 'is-soldout': variantMax(variant) < 1 }">
            {{ variant.variant_name }}<span v-if="variantMax(variant) < 1" class="soldout-tag">（庫存不足）</span>
          </div>
        </div>


        <!-- 數量控制區 -->
        <div class="qty-section">
          <div class="quantity-control">
            <button @click="decreaseQty" :disabled="localQuantity <= 1">-</button>
            <input inputmode="numeric" type="number" v-model.number="localQuantity" min="1" max="99"
              @blur="checkQuantity" />
            <button @click="increaseQty" :disabled="localQuantity >= qtyMax">+</button>
          </div>
          <span class="qty-hint">(最多可選 {{ qtyMax }} 件)</span>
        </div>
        <p v-if="errorMsg" class="error-text">{{ errorMsg }}</p>
        <!-- 加入購物車 / 立即購買 -->
        <div class="action-buttons">
          <button class="confirm-add-cart" @click="emitAddToCart" :disabled="productDetails.stock === 0 || selectedShort">
            {{ productDetails.stock === 0 ? ' (已售完)' : (selectedShort ? '庫存不足' : '加入購物車') }}
          </button>
          <button class="buy-now-button" @click="emitBuyNow" :disabled="productDetails.stock === 0 || selectedShort">
            立即購買
          </button>
        </div>
        <!-- 購買前告知：個人衛生用品拆封後可能不適用 7 日解除權（法規要求購買前明確告知） -->
        <p class="hygiene-notice">面膜、安瓶、潔顏慕斯屬個人衛生用品，拆除衛生密封包裝後，依法可能不適用 7 日鑑賞期退貨。詳見<router-link to="/productreturns" target="_blank">〈商品退換貨〉</router-link></p>
      </div>
    </div>

    <!-- 上方區域：Tab 按鈕 -->
    <div class="detail-tabs">
      <button v-if="productDetails.detail_html" :class="{ active: activeTab === 'detail' }" @click="scrollToDetail">
        詳細說明
      </button>
      <button v-if="productDetails.spec_html" :class="{ active: activeTab === 'spec' }" @click="scrollToRecommend">
        規格說明
      </button>
    </div>
    <!-- 下方：商品介紹 & 規格說明 -->
    <div class="detail-bottom-section">
      <div ref="detailSection" v-if="productDetails.detail_html" class="detail-html"
        v-html="productDetails.detail_html">
      </div>
      <hr v-if="productDetails.spec_html" class="section-divider" />
      <div ref="recommendSection" v-if="productDetails.spec_html" class="spec-html" v-html="productDetails.spec_html">
      </div>
    </div>
  </div>
</template>

<!-- <script>
import { useSysTempStore } from '@/store/sysTempStore'
import { mapActions, mapState, mapGetters } from 'vuex'
import { getMediaType, getYoutubeId } from '@/utils/mediaUtil.js'
import { useToastStore } from '@/store/toastStore.js'
import { useCartStore } from '@/store/cartStore'
import axios from 'axios'

export default {
  name: 'ProductDetailPage',
  data() {
    return {
      isMobile: false,
      productDetails: null,
      currentIndex: 0,
      shippingMethods: [],
      lensVisible: false,
      lensStyle: {},
      resultStyle: {},
      localQuantity: 1,
      activeTab: 'detail', // 預設顯示「詳細說明」
      errorMsg: '',   // 新增，預設空字串
      // 用來記錄「目前選中」的規格。如果 = null，代表尚未選擇
      selectedVariant: null
    }
  },
  setup() {
    const toastStore = useToastStore()
    const cartStore = useCartStore()

    
    // 這裡把 toastStore return，才能在後續的 this.toastStore 中使用
    return {
      toastStore,
      cartStore,

    }
  },
  watch: {
    currentIndex() {
      this.$nextTick(() => {
        if (this.currentMedia.media_type === 'facebook' && window.FB && window.FB.XFBML) {
          window.FB.XFBML.parse()
        }
      })
    },
  },
  computed: {
    ...mapState(['cartItems']),
    ...mapGetters(['cartCount', 'cartTotal']),
    currentMedia() {
      if (!this.productDetails || !this.productDetails.pMedias) return {}
      return this.productDetails.pMedias[this.currentIndex] || {}
    },
    // 是否可以往前
    hasPrev() {
      return this.currentIndex > 0
    },
    // 是否可以往後
    hasNext() {
      return (
        this.productDetails &&
        this.productDetails.pMedias &&
        this.currentIndex < this.productDetails.pMedias.length - 1
      )
    },
  },
  mounted() {
    // 監聽視窗大小改變，動態更新 isMobile
    window.addEventListener('resize', this.checkIsMobile)
  },
  beforeUnmount() {
    // 記得在元件銷毀前，移除監聽，避免事件洩漏
    window.removeEventListener('resize', this.checkIsMobile)
  },
  created: async function () {
    this.checkIsMobile()
    const useSysTemp= useSysTempStore()
    await useSysTemp.fetchShippingData()
    this.shippingMethods = useSysTemp.shippingMethods

    // 進入商品詳情頁時，去撈商品詳細資訊
    this.fetchProductDetails()
  },

  methods: {
    ...mapActions([
      'addProductToCart'
    ]),
    checkIsMobile() {
      // <= 768 為手機或小螢幕
      this.isMobile = window.innerWidth <= 768
    },
    scrollToDetail() {
      this.activeTab = 'detail';
      this.$refs.detailSection.scrollIntoView({ behavior: 'smooth' })
    },
    scrollToRecommend() {
      this.activeTab = 'spec';
      this.$refs.recommendSection.scrollIntoView({ behavior: 'smooth' })
    },
    prevMedia() {
      if (this.hasPrev) {
        this.currentIndex--
      }
    },
    nextMedia() {
      if (this.hasNext) {
        this.currentIndex++
      }
    },
    // 撈取商品詳情
    fetchProductDetails() {
      //console.log(this.productId);
      const routeId = this.$route.params.id
      console.log(routeId)
      // 以 GET 方式帶 productId
      axios
        .get(`/api/load/productD/${routeId}`)
        .then(response => {
          //console.log(response.data)
          if (response.data.success) {
            if (response.data.pMedias.length > 0) {
              response.data.pMedias.forEach(media => {
                media.media_type = getMediaType(media.media_url)
                media.youtube_id = getYoutubeId(media.media_url)
              })
            }
            // 後端回傳 pDetails, pMedias
            this.productDetails = {
              ...response.data.pDetails,
              pMedias: response.data.pMedias
            }

            console.log(this.productDetails.seo_description)

            // 如果只有1個 variant → 自動選取
            if (
              this.productDetails.Variants &&
              this.productDetails.Variants.length === 1
            ) {
              this.selectedVariant = this.productDetails.Variants[0]
            }

            this.$nextTick(() => {
              if (window.FB && window.FB.XFBML) {
                window.FB.XFBML.parse()
              }
            })
          } else {
            console.error(response.data.message)
          }
        })
        .catch(err => {
          console.error(err)
        })
    },

    // 加入購物車
    emitAddToCart() {
      // 1. 若尚未選規格(且 Variants > 1)，可提醒
      //    （若該商品根本就只有1個規格，已自動選取）
      if (
        this.productDetails.Variants &&
        this.productDetails.Variants.length > 1 &&
        !this.selectedVariant
      ) {
        // this.toastStore.addToast(`請先選擇規格！`, 'warning');
        this.errorMsg = '請先選擇規格！';
        return
      }

      // 2. 組裝要放進購物車的資料
      // 注意：可以依照你實際後端 / 前端需求來決定裝哪些欄位
      // selectedVariant 若不存在，就用默認 productDetails (無 variant)  
      // → 不過通常多規格商品一定要有 Variant
      let currentVariant = this.selectedVariant || {}
      const payload = {
        description: this.productDetails.description,
        image_url: this.productDetails.image_url,
        title: this.productDetails.title,
        product_id: this.productDetails.product_id,
        variant_id: currentVariant.variant_id || 0, // or '' 
        variant_name: currentVariant.variant_name || '',
        discount_price: currentVariant.discount_price || this.productDetails.discount_price,
        quantity: this.localQuantity,
      }

      this.addProductToCart({
        product: payload,
        quantity: this.localQuantity
      })
      this.errorMsg = ''
      this.toastStore.addToast(`成功加入購物車：${this.productDetails.title}`, 'success')
      this.cartStore.toggleCartDropdown(true)
      setTimeout(() => {
        this.cartStore.toggleCartDropdown(false)
      }, 2000)

    },

    // 立即購買
    emitBuyNow() {
      // 1. 若尚未選規格(且 Variants > 1)，可提醒
      if (
        this.productDetails.Variants &&
        this.productDetails.Variants.length > 1 &&
        !this.selectedVariant
      ) {
        this.errorMsg = '請先選擇規格！';
        return;
      }

      // 2. 組裝「購物車商品」資料
      let currentVariant = this.selectedVariant || {};
      const payload = {
        description: this.productDetails.description,
        image_url: this.productDetails.image_url,
        title: this.productDetails.title,
        product_id: this.productDetails.product_id,
        variant_id: currentVariant.variant_id || 0,
        variant_name: currentVariant.variant_name || '',
        discount_price: currentVariant.discount_price
          || this.productDetails.discount_price,
        quantity: this.localQuantity,
      };

      // 3. 呼叫 Vuex / Pinia 的 Action，先把商品加到購物車 
      //    (也可以先清空購物車再加，如果你想「BuyNow」單純只買這項)
      this.addProductToCart({
        product: payload,
        quantity: this.localQuantity
      });

      // 4. 清空錯誤訊息
      this.errorMsg = '';

      // 5. 顯示提示或 Toast（可省略）
      this.toastStore.addToast(`已立即購買：${this.productDetails.title}`, 'success');

      // 6. 跳轉到購物車頁面
      this.$router.push('/cart');
    },


    // 選擇某個規格
    selectVariant(variant) {
      this.selectedVariant = variant
    },
    // 放大鏡
    moveLens(e) {
      if (this.isMobile || this.currentMedia.media_type !== 'image') return

      this.lensVisible = true
      let x = e.offsetX
      let y = e.offsetY

      const lensSize = 60
      if (x < lensSize) x = lensSize
      if (y < lensSize) y = lensSize

      const imgEl = this.$refs.mainImgRef
      const rect = imgEl.getBoundingClientRect()

      if (x > rect.width - lensSize) x = rect.width - lensSize
      if (y > rect.height - lensSize) y = rect.height - lensSize

      this.lensStyle = {
        display: 'block',
        top: (y - lensSize) + 'px',
        left: (x - lensSize) + 'px',
        width: (lensSize * 2) + 'px',
        height: (lensSize * 2) + 'px'
      }

      const cx = 2
      const cy = 2
      const backgroundImage = `url(${this.currentMedia.media_url})`
      const backgroundSize = `${rect.width * cx}px ${rect.height * cy}px`
      const backgroundPosX = -(x * cx - lensSize) + 'px'
      const backgroundPosY = -(y * cy - lensSize) + 'px'

      // 判斷要顯示在右側或左側
      const containerRect = this.$refs.magnifierWrapper.getBoundingClientRect()
      const resultWidth = 200
      let resultLeft = 'calc(100% + 10px)'
      if (containerRect.right + 10 + resultWidth > window.innerWidth) {
        resultLeft = `-${resultWidth + 10}px`
      }

      this.resultStyle = {
        left: resultLeft,
        backgroundImage,
        backgroundRepeat: 'no-repeat',
        backgroundSize,
        backgroundPosition: `${backgroundPosX} ${backgroundPosY}`
      }
    },
    hideLens() {
      this.lensVisible = false
      this.lensStyle.display = 'none'
    },
    getYoutubeThumbnail(youtubeId) {
      return `https://img.youtube.com/vi/${youtubeId}/0.jpg`
    },

    // 數量控制
    decreaseQty() {
      if (this.localQuantity > 1) this.localQuantity--
    },
    increaseQty() {
      if (this.localQuantity < 99) this.localQuantity++
    },
    checkQuantity() {
      if (!this.localQuantity || this.localQuantity < 1) {
        this.localQuantity = 1
      } else if (this.localQuantity > 99) {
        this.localQuantity = 99
      }
    },
  }
}
</script> -->

<!-- filepath: /d:/html/VUE/jili/src/views/ProductDetail.vue -->
<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import axios from 'axios'
import { parseFbVideos } from '@/utils/fbSdk.js'
import { loadCoupons } from '@/utils/promoData'
import { maxQtyForVariant, stockShortMessage, checkStockRemote } from '@/utils/stock'
import { useStore } from 'vuex'
import { useSysTempStore } from '@/store/sysTempStore'
import { useToastStore } from '@/store/toastStore.js'
import { useCartStore } from '@/store/cartStore'
import { getMediaType, getYoutubeId } from '@/utils/mediaUtil.js'
import { useRoute, useRouter } from 'vue-router'
import { useHead } from '@vueuse/head'
import { pixelViewContent } from '@/plugins/metaPixel'

const route = useRoute()
const router = useRouter()

/* 導入 Pinia Store */
const toastStore = useToastStore()
const cartStore = useCartStore()
const useSysTemp = useSysTempStore()

/* 取得 Vuex 的 store，若仍需用到 mapState、mapGetters、mapActions，可在此自行處理 */
const store = useStore()

/* 原 data() 內屬性 → 改用 ref / reactive */
const isMobile = ref(false)
const productDetails = ref(null)

// 首次進站、資料還在載入時，先用伺服器注入的 og:title 當 h1（搜尋引擎可能在載入中就拍照）
function serverPageTitle() {
  try {
    const url = (document.querySelector('meta[property="og:url"]') || {}).content || ''
    if (url.replace(/\/$/, '') !== (location.origin + location.pathname).replace(/\/$/, '')) return ''
    const t = (document.querySelector('meta[property="og:title"]') || {}).content || ''
    return t.replace(/｜JiLi 肌黎$/, '')
  } catch (e) { return '' }
}
const loadingH1 = serverPageTitle()

const currentIndex = ref(0)
const shippingMethods = ref([])
const lensVisible = ref(false)
const lensStyle = ref({})
const resultStyle = ref({})
const localQuantity = ref(1)
const activeTab = ref('detail')
const errorMsg = ref('')
const selectedVariant = ref(null)

// 折扣券
const coupons = ref([])

const bestCoupon = computed(() => {
  const price = selectedVariant.value?.discount_price ?? productDetails.value?.discount_price ?? 0
  const applicable = coupons.value.filter(c => Number(price) >= c.min_amount)
  if (!applicable.length) return null
  // 折數越小 = 折扣越大，選折數最小的
  return applicable.reduce((best, c) => Number(c.discount_rate) < Number(best.discount_rate) ? c : best)
})

const couponAfterPrice = computed(() => {
  if (!bestCoupon.value || !productDetails.value) return 0
  const price = selectedVariant.value?.discount_price ?? productDetails.value?.discount_price ?? 0
  return Math.round(Number(price) * Number(bestCoupon.value.discount_rate) / 10)
})

const mainImgRef = ref(null)
const magnifierWrapper = ref(null)

const detailSection = ref(null)
const recommendSection = ref(null)


/* 原 watch: currentIndex */
watch(currentIndex, () => {
  nextTick(() => {
    if (currentMedia.value.media_type === 'facebook') parseFbVideos()
  })
})

/* 原 computed */
const currentMedia = computed(() => {
  if (!productDetails.value || !productDetails.value.pMedias) return {}
  return productDetails.value.pMedias[currentIndex.value] || {}
})
const hasPrev = computed(() => currentIndex.value > 0)
const hasNext = computed(() =>
  productDetails.value &&
  productDetails.value.pMedias &&
  currentIndex.value < productDetails.value.pMedias.length - 1
)

/* 生命周期 */
onMounted(async () => {
  window.addEventListener('resize', checkIsMobile)
  checkIsMobile()
  await useSysTemp.fetchShippingData()
  shippingMethods.value = useSysTemp.shippingMethods
  await fetchProductDetails()
  // 載入折扣券
  try {
    coupons.value = await loadCoupons()
  } catch (e) { /* 靜默失敗 */ }
})
onBeforeUnmount(() => {
  window.removeEventListener('resize', checkIsMobile)
})

/* 原 methods */
function checkIsMobile() {
  isMobile.value = window.innerWidth <= 768
}
function scrollToDetail() {
  activeTab.value = 'detail'
  if (detailSection.value) {
    detailSection.value.scrollIntoView({ behavior: 'smooth' })
  }
}
function scrollToRecommend() {
  activeTab.value = 'spec'
  if (recommendSection.value) {
    recommendSection.value.scrollIntoView({ behavior: 'smooth' })
  }
}
function prevMedia() {
  if (hasPrev.value) currentIndex.value--
}
function nextMedia() {
  if (hasNext.value) currentIndex.value++
}
async function fetchProductDetails() {
  const routeId = route.params.id
  axios.get(`/api/load/productD/${routeId}`)
    .then(response => {
      if (response.data.success) {
        if (response.data.pMedias.length > 0) {
          response.data.pMedias.forEach(media => {
            media.media_type = getMediaType(media.media_url)
            media.youtube_id = getYoutubeId(media.media_url)
          })
        }
        productDetails.value = {
          ...response.data.pDetails,
          pMedias: response.data.pMedias
        }
        if (productDetails.value.Variants && productDetails.value.Variants.length === 1) {
          selectedVariant.value = productDetails.value.Variants[0]
        }
        // Meta Pixel：瀏覽商品事件
        pixelViewContent({
          id: productDetails.value.product_id,
          name: productDetails.value.title,
          price: selectedVariant.value?.discount_price
            ?? productDetails.value.discount_price
            ?? productDetails.value.Variants?.[0]?.discount_price
        })
        updateSeoDescription();
        nextTick(() => {
          parseFbVideos()
        })
      } else {
        console.error(response.data.message)
      }
    })
    .catch(err => {
      console.error(err)
    })
}

async function updateSeoDescription() {
  if(productDetails.value.seo_description){
    // 與伺服器注入的標題一致（Google 執行網頁程式後看到的也是這個）
    const docTitle = (productDetails.value.seo_title || productDetails.value.title || '') + '｜JiLi 肌黎'

    // 商品結構化資料改由伺服器（server.js /product/:id）產生一份，含即時價格與庫存；這裡不再重複產生
    useHead({
      title: docTitle,
      meta: [
          {
            name: 'description',
            content: productDetails.value.seo_description
          },
          {
            property: 'og:description',
            content: productDetails.value.seo_description
          },
          {
            property: 'og:title',
            content: docTitle
          },
      ]
    })
  }
}
// 庫存：每個規格最多還能加幾組（扣掉購物車已有的同商品）
function variantMax(variant) {
  return maxQtyForVariant(store.state.cartItems, productDetails.value, variant)
}
const qtyMax = computed(() => selectedVariant.value ? Math.max(1, variantMax(selectedVariant.value)) : 99)
const selectedShort = computed(() => !!selectedVariant.value && variantMax(selectedVariant.value) < 1)
function stockOk() {
  if (selectedVariant.value && localQuantity.value > variantMax(selectedVariant.value)) {
    errorMsg.value = stockShortMessage(store.state.cartItems, productDetails.value)
    return false
  }
  return true
}
// 畫面上的庫存可能是很久以前載入的 → 加入前再向伺服器確認（含購物車已有的）
let stockChecking = false
async function remoteStockOk() {
  const v = selectedVariant.value
  if (!v) return true
  const r = await checkStockRemote([
    ...(store.state.cartItems || []),
    { product_id: productDetails.value?.product_id, variant_id: v.variant_id, variant_name: v.variant_name, quantity: localQuantity.value }
  ])
  if (!r.ok) errorMsg.value = r.message || '庫存不足，請重新整理頁面'
  return r.ok
}
async function emitAddToCart() {
  if (
    productDetails.value?.Variants?.length > 1 &&
    !selectedVariant.value
  ) {
    errorMsg.value = '請先選擇規格！'
    return
  }
  if (!stockOk() || stockChecking) return
  stockChecking = true
  try { if (!(await remoteStockOk())) return } finally { stockChecking = false }
  let currentVariant = selectedVariant.value || {}
  const payload = {
    description: productDetails.value?.description,
    image_url: productDetails.value?.image_url,
    title: productDetails.value?.title,
    product_id: productDetails.value?.product_id,
    variant_id: currentVariant.variant_id || 0,
    variant_name: currentVariant.variant_name || '',
    discount_price: currentVariant.discount_price || productDetails.value?.discount_price,
    quantity: localQuantity.value,
  }
  store.dispatch('addProductToCart', {
    product: payload,
    quantity: localQuantity.value
  })
  errorMsg.value = ''
  toastStore.addToast(`成功加入購物車：${productDetails.value?.title}`, 'success')
  cartStore.toggleCartDropdown(true)
  setTimeout(() => {
    cartStore.toggleCartDropdown(false)
  }, 2000)
}
async function emitBuyNow() {
  if (
    productDetails.value?.Variants?.length > 1 &&
    !selectedVariant.value
  ) {
    errorMsg.value = '請先選擇規格！'
    return
  }
  if (!stockOk() || stockChecking) return
  stockChecking = true
  try { if (!(await remoteStockOk())) return } finally { stockChecking = false }
  let currentVariant = selectedVariant.value || {}
  const payload = {
    description: productDetails.value?.description,
    image_url: productDetails.value?.image_url,
    title: productDetails.value?.title,
    product_id: productDetails.value?.product_id,
    variant_id: currentVariant.variant_id || 0,
    variant_name: currentVariant.variant_name || '',
    discount_price: currentVariant.discount_price || productDetails.value?.discount_price,
    quantity: localQuantity.value,
  }
  store.dispatch('addProductToCart', {
    product: payload,
    quantity: localQuantity.value
  })
  errorMsg.value = ''
  toastStore.addToast(`已立即購買：${productDetails.value?.title}`, 'success')
  router.push('/cart')
}
function selectVariant(variant) {
  selectedVariant.value = variant
  errorMsg.value = ''
  if (localQuantity.value > qtyMax.value) localQuantity.value = qtyMax.value
}
function moveLens(e) {
  if (isMobile.value || currentMedia.value.media_type !== 'image') return
  lensVisible.value = true
  let x = e.offsetX
  let y = e.offsetY
  const lensSize = 60
  if (x < lensSize) x = lensSize
  if (y < lensSize) y = lensSize

  const imgEl = mainImgRef.value
  const rect = imgEl?.getBoundingClientRect()
  if (!rect) return
  if (x > rect.width - lensSize) x = rect.width - lensSize
  if (y > rect.height - lensSize) y = rect.height - lensSize

  lensStyle.value = {
    display: 'block',
    top: (y - lensSize) + 'px',
    left: (x - lensSize) + 'px',
    width: (lensSize * 2) + 'px',
    height: (lensSize * 2) + 'px'
  }
  const cx = 2
  const cy = 2
  const backgroundImage = `url(${currentMedia.value.media_url})`
  const backgroundSize = `${rect.width * cx}px ${rect.height * cy}px`
  const backgroundPosX = -(x * cx - lensSize) + 'px'
  const backgroundPosY = -(y * cy - lensSize) + 'px'
  const containerRect = magnifierWrapper.value?.getBoundingClientRect()
  const resultWidth = 200
  let resultLeft = 'calc(100% + 10px)'
  if (containerRect && containerRect.right + 10 + resultWidth > window.innerWidth) {
    resultLeft = `-${resultWidth + 10}px`
  }
  resultStyle.value = {
    left: resultLeft,
    backgroundImage,
    backgroundRepeat: 'no-repeat',
    backgroundSize,
    backgroundPosition: `${backgroundPosX} ${backgroundPosY}`
  }
}
function hideLens() {
  lensVisible.value = false
  lensStyle.value.display = 'none'
}
function getYoutubeThumbnail(youtubeId) {
  return `https://img.youtube.com/vi/${youtubeId}/0.jpg`
}
function decreaseQty() {
  if (localQuantity.value > 1) localQuantity.value--
}
function increaseQty() {
  if (localQuantity.value < qtyMax.value) localQuantity.value++
}
function checkQuantity() {
  if (!localQuantity.value || localQuantity.value < 1) {
    localQuantity.value = 1
  } else if (localQuantity.value > qtyMax.value) {
    localQuantity.value = qtyMax.value
  }
}
</script>
<style scoped>
@media (max-width: 768px) {
  .detail-top-section {
    flex-direction: column;
    /* 改成上下排列 */
  }

  /* 也可確保左右兩側都佔滿寬度 */
  .detail-media,
  .detail-info {
    width: 100%;
    max-width: 100%;
  }

  /* 如果主視圖有固定寬高，可在小螢幕下取消或縮小 */
  .main-display {
    width: 100%;
    height: auto;
    /* 讓圖片高度自動，避免小螢幕被硬塞 450px */
  }
}


.product-detail-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 16px;
}

/* 與 CartModal 類似，可視需求精簡 */
.detail-top-section {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}

.detail-media,
.detail-info {
  flex: 1;
}

.main-display {
  width: 450px;
  height: 450px;
  position: relative;
  overflow: visible;
}

@media (max-width: 768px) {
  .main-display {
    width: 100% !important;
    height: auto !important;
  }
}

.main-display img,
.main-display iframe,
.main-display video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 價格 */
.price {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 16px;
}

.price del {
  color: #999;
  font-size: 1.2rem;
  text-decoration: line-through;
}

.discount-price {
  color: #e50000;
  font-size: 2.5rem;
  font-weight: bold;
}

/* 折扣券提示 */
.pd-coupon-hint {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  background: #fff3e8;
  border: 1.5px solid #ffb347;
  border-radius: 8px;
  padding: 7px 14px;
  margin-bottom: 14px;
  font-size: 0.9rem;
  color: #cc4400;
}
.pd-coupon-hint strong {
  font-size: 1.1rem;
  font-weight: 700;
}
.pd-coupon-hint span {
  color: #999;
  font-size: 0.8rem;
}
.pd-coupon-expire {
  color: #c0392b;
  font-size: 0.78rem;
  font-weight: 700;
  margin-left: 6px;
}

/* 載入中 */
.pd-loading-title {
  font-size: 1.3rem;
  margin: 0 0 12px;
  text-align: center;
}
.pd-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 40vh;
  gap: 16px;
  color: #aaa;
  font-size: 1rem;
}
.pd-spinner {
  width: 36px; height: 36px;
  border: 4px solid #eee;
  border-top-color: #ff6600;
  border-radius: 50%;
  animation: pd-spin .8s linear infinite;
  display: block;
}
@keyframes pd-spin { to { transform: rotate(360deg); } }

/* 運送 & 付款方式 */
.shipping-info {
  margin-bottom: 16px;
}

.shipping-info h4 {
  font-size: 1rem;
  margin-bottom: 6px;
  font-weight: bold;
}

.shipping-info ul {
  margin: 0;
  padding: 0;
  list-style: none;
}

.shipping-info li {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

.shipping-info li img {
  width: 24px;
  height: 24px;
  object-fit: contain;
  margin-right: 8px;
}

/* 數量控制 */
.quantity-control {
  display: inline-flex;
  border: 1px solid #ccc;
  /* 外框 */
  border-radius: 4px;
  /* 圆角 */
  overflow: hidden;
  /* 讓子元素被裁切，不超出外框 */
  /* 依需求可微調寬度 / 高度 */
  height: 40px;
  /* 讓三個子元素 - / 輸入框 / + 一起排成一行 */
}

/* 左側、右側按鈕都通用 */
.quantity-control button {
  width: 40px;
  background: #f5f5f5;
  /* 淺灰色底，可依需求修改 */
  border: none;
  outline: none;
  cursor: pointer;
  font-size: 1.2rem;
  color: #444;
  /* 文字顏色 */
  /* ★ 關鍵：垂直置中 */
  line-height: 40px;
  /* 或 40px; */
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 若按鈕被 disabled，就顯示為灰色 */
.quantity-control button:disabled {
  color: #999;
  cursor: not-allowed;
}

/* 中間輸入框 */
.quantity-control input {
  width: 60px;
  /* 可依需求調整 */
  border: none;
  text-align: center;
  font-size: 1rem;
  outline: none;
  /* 移除瀏覽器的聚焦邊框 */
  /* 可能也要關閉 spin button，避免出現預設微調箭頭 */
  -moz-appearance: textfield;
  appearance: textfield;
}

.quantity-control input::-webkit-inner-spin-button,
.quantity-control input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.qty-section {
  display: flex;
  align-items: center;
  gap: 8px;
  /* 與 hint 保持 8px 間距 */
}

.qty-hint {
  font-size: 0.85rem;
  color: #999;
  /* 也可加 margin-left: 8px; 就不需要 gap 了 */
}


/* 按鈕 */
.action-buttons {
  margin-top: 16px;
  display: flex;
  gap: 10px;
}

.confirm-add-cart {
  background: #ff6600;
  color: #fff;
  border: none;
  padding: 10px 16px;
  cursor: pointer;
  border-radius: 4px;
  flex: none;
}

.confirm-add-cart:hover {
  background: #ff5500;
}

.buy-now-button {
  background: #ff0000;
  color: #fff;
  border: none;
  padding: 10px 16px;
  cursor: pointer;
  border-radius: 4px;
  flex: none;
}

.buy-now-button:hover {
  background: #cc0000;
}

.confirm-add-cart:disabled,
.confirm-add-cart:disabled:hover,
.buy-now-button:disabled,
.buy-now-button:disabled:hover {
  background: #bdbdbd;
  color: #fff;
  cursor: not-allowed;
}

.pd-batch-dates {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  font-size: 0.88rem;
  color: #555;
  margin: 8px 0 4px;
}
.hygiene-notice {
  font-size: 0.8rem;
  color: #888;
  line-height: 1.6;
  margin: 10px 0 0;
}
.hygiene-notice a {
  color: #888;
  text-decoration: underline;
}
/* 放大鏡 */
.magnifier-container {
  position: relative;
  display: inline-block;
  cursor: zoom-in;
}

.magnifier-lens {
  position: absolute;
  border: 2px solid #999;
  border-radius: 0;
  display: none;
  opacity: 0.3;
  pointer-events: none;
  z-index: 1002;
}

.magnifier-result {
  position: absolute;
  top: 0;
  left: calc(100% + 10px);
  width: 450px;
  height: 450px;
  border: 2px solid #999;
  border-radius: 4px;
  background-color: #fff;
  z-index: 1200;
  pointer-events: none;
}

/* 縮略圖 */
.thumbnail-list {
  margin-top: 10px;
  display: flex;
  gap: 5px;
  justify-content: center;
}

.thumbnail {
  width: 60px;
  height: 60px;
  overflow: hidden;
  border: 1px solid #ddd;
  cursor: pointer;
}

.thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.detail-bottom-section {
  margin-top: 20px;
}

.detail-html,
.spec-html {
  margin-bottom: 20px;
}

/* Chrome、Safari、Edge */
input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type="number"] {
  -moz-appearance: textfield;
}


.arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.7);
  border: none;
  font-size: 2rem;
  padding: 0.5rem 1rem;
  cursor: pointer;
  z-index: 999;
  /* 讓按鈕在最上層 */
}

.arrow-left {
  left: 0;
}

.arrow-right {
  right: 0;
}

.arrow:hover {
  background: rgba(255, 255, 255, 0.9);
}


.detail-tabs {
  display: flex;
  border-bottom: 1px solid #ccc;
  /* 底線 */
  margin-bottom: 1em;
  /* 與下方內容的間距 */
}

.detail-tabs button {
  background: none;
  border: none;
  outline: none;
  padding: 0.75em 1.5em;
  cursor: pointer;
  font-size: 1rem;
  color: #555;
  transition: color 0.2s;
}

/* 滑鼠移上去時，改變文字顏色(或底線樣式) */
.detail-tabs button:hover {
  color: #333;
}

/* 被選到的 Tab 顯示強調 */
.detail-tabs .active {
  color: #000;
  border-bottom: 2px solid #000;
}

/* 底下區塊樣式 */
.detail-bottom-section {
  margin-top: 1em;
}

.detail-html,
.spec-html {
  /* 可以依實際情況增加排版、字體大小、留白等 */
  margin-bottom: 2em;
  line-height: 1.6;
  color: #333;
}

.section-divider {
  border: none;
  border-top: 5px solid #ddd;
  margin: 40px 0;
}

.section-gap {
  margin: 40px 0;
  /* 或 padding: 40px 0; */
  /* 也可放個分隔圖、或自訂背景色 */
}
</style>

<style scoped>
.breadcrumb {
  margin-bottom: 16px;
  /* 或 20px 等你覺得合適的距離 */
}
</style>

<style scoped>

.video-container {
  position: relative;
  width: 100%;
  padding-bottom: 56.25%;
  height: 0;
  overflow: hidden;
}

.video-container iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.detail-html,
.spec-html {
  word-wrap: break-word;
  /* 支援舊版瀏覽器 */
  overflow-wrap: break-word;
  /* 更現代屬性 */
}

.responsive-iframe {
  width: 100%;
  aspect-ratio: 16 / 9;
  /* 16:9比例 */
  border: 0;
}

@media (max-width: 768px) {
  .quantity-control button {
    /* 改為更大的最小尺寸 */
    min-width: 50px;
    /*min-height: 50px;*/
    font-size: 1.2rem;
  }

  .quantity-control input {
    width: 70px;
    /* 若不足，可再調大 */
    min-width: 70px;
    /* 防止壓縮 */
    font-size: 1.2rem;
  }
}

.product-desc {
  font-size: 1.2rem;
  color: #777;
  margin-bottom: 8px;
  line-height: 1.4;
  text-align: left;
  /* 文字靠左 */
}
</style>
<style scoped>
.video-container {
  max-width: 100%;
  max-height: 160vh;
  overflow: hidden;
}

.video-container .fb-video,
.video-container .fb-video>span,
.video-container .fb-video iframe {
  width: 100% !important;
  height: auto !important;
}



/* 規格選擇 */
.variant-choices {
  text-align: left;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}

.variant-choices p {
  width: 100%;
  margin-bottom: 4px;
  font-weight: bold;
}

.variant-item {
  display: inline-block;
  padding: 6px 12px;
  border: 1px solid #ddd;
  cursor: pointer;
  border-radius: 4px;
}

.variant-item:hover {
  background: #f3f3f3;
}

.variant-item.is-soldout {
  opacity: 0.5;
}
.soldout-tag {
  font-size: 0.85em;
  color: #c0392b;
}
.variant-item.is-active {
  border-color: #f90;
  background: #fff1e6;
}

.error-text {
  width: 350px;
  background: #fff7c0;
  /* 淺黃色 */
  padding: 10px 15px;
  color: #e50000;
  border-radius: 4px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
}
</style>
<style scoped>
/**
 * 商品庫存 / 已售出
 */
.stock-sold-wrapper {
  display: flex;
  gap: 20px;
  margin: 12px 0;

  .info-box {
    display: flex;
    align-items: center;
    padding: 6px 12px;
    border-radius: 6px;
    background: linear-gradient(120deg, #fff 0%, #f3f3f3 100%);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    transition: box-shadow 0.3s ease, background 0.3s ease;
    cursor: default;

    i {
      margin-right: 6px;
      font-size: 1.2rem;
      color: #444;
      transition: color 0.3s;
    }

    .number {
      font-size: 1.3rem;
      font-weight: bold;
      margin-right: 4px;
      color: #222;
    }

    .label {
      font-size: 0.85rem;
      color: #666;
    }

    &:hover {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      background: linear-gradient(120deg, #fafafa 0%, #f0f0f0 100%);

      i {
        color: #ff6600;
      }
    }
  }

  .sold-box {
    background: linear-gradient(120deg, #fff5f2 0%, #fce6e0 100%);

    &:hover {
      background: linear-gradient(120deg, #feece7 0%, #fbded6 100%);
    }

    i {
      color: #e74c3c;
    }
  }

  .stock-box {
    background: linear-gradient(120deg, #f5fff2 0%, #e4fce0 100%);

    &:hover {
      background: linear-gradient(120deg, #ebffe6 0%, #d5fcd2 100%);
    }

    i {
      color: #27ae60;
    }
  }
}
</style>
<style>
.my-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 16px;
}
.my-row {
  display: flex;
  flex-wrap: wrap;
}
.my-col-1 {
  flex: 0 0 8.333%;
}
.my-col-2 {
  flex: 0 0 16.666%;
}
.my-col-3 {
  flex: 0 0 25%;
}
.my-col-4 {
  flex: 0 0 33.333%;
}
.my-col-5 {
  flex: 0 0 41.666%;
}
.my-col-6 {
  flex: 0 0 50%;
}
.my-col-7 {
  flex: 0 0 58.333%;
}
.my-col-8 {
  flex: 0 0 66.666%;
}
.my-col-9 {
  flex: 0 0 75%;
}
.my-col-10 {
  flex: 0 0 83.333%;
}
.my-col-11 {
  flex: 0 0 91.666%;
}
.my-col-12 {
  flex: 0 0 100%;
}



@media (max-width: 768px) {
  .my-col-6 {
    flex: 0 0 100%;
  }
  .my-col-4 {
    flex: 0 0 50%;
  }
  .my-col-3 {
    flex: 0 0 33%;
  }
  .my-col-2 {
    flex: 0 0 25%;
  }
}
.my-heading {
  font-size: 1.5rem;
  margin-bottom: 0.75rem;
  font-weight: bold;
}

/* 文字或段落相關 */
.my-text-body {
  font-size: 1rem;
  line-height: 1.6;
  color: #333;
  margin-bottom: 1rem;
}
.my-text-muted {
  color: #999;
  font-size: 0.9rem;
}
.my-text-center {
  text-align: center;
}
.my-text-right {
  text-align: right;
}
.my-text-left {
  text-align: left;
}

/* 
  圖片自適應 
*/
.my-img-fluid {
  max-width: 100%;
  height: auto;
  display: block; 
}

/* 
  間距工具 (Spacing Utilities)
  e.g. .my-mb-3 -> margin-bottom: 1rem;
       .my-mt-2 -> margin-top: 0.5rem;
*/
.my-mb-1 {
  margin-bottom: 0.25rem !important;
}
.my-mb-2 {
  margin-bottom: 0.5rem !important;
}
.my-mb-3 {
  margin-bottom: 1rem !important;
}
.my-mb-4 {
  margin-bottom: 1.5rem !important;
}

.my-mt-1 {
  margin-top: 0.25rem !important;
}
.my-mt-2 {
  margin-top: 0.5rem !important;
}
.my-mt-3 {
  margin-top: 1rem !important;
}
.my-mt-4 {
  margin-top: 1.5rem !important;
}
.my-card {
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 16px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-bottom: 1rem;
}
.my-card-header {
  font-size: 1.2rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
}
.my-card-body {
  /* 內容 */
}
.my-card-footer {
  margin-top: 1rem;
  border-top: 1px solid #eee;
  padding-top: 0.5rem;
  text-align: right;
}

.detail-html img,
.detail-html video,
.spec-html img,
.spec-html iframe,
.spec-html video {
  max-width: 100%;
  height: auto;
  display: block;
  /* 讓
  它獨佔一行，避免文字環繞導致排版錯亂 */
}

.detail-html iframe ,
.spec-html iframe{
  width: 500px;
  height: 550px;
}

.my-iframe-height{
  min-height: 500px;
  min-width: 550px;
}


@media (max-width: 768px) {
  .my-iframe-height {
    min-width: auto; /* or 0 */
  }
  .detail-html iframe {
    display: block;
    margin: 0 auto;
    max-width: 100%;
    /*height: 550px;*/
  }
}
</style>